/**
 * Table displaying confirmed show dates with operational data
 */

import React, { useState } from 'react';
import {
  Stack,
  Typography,
  Box,
  CircularProgress,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Radio,
  Chip,
  Alert,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { MobileHoldCard } from './MobileHoldCard';
import { useNavigate } from 'react-router-dom';
import type { HoldList, Hold } from 'api-client/models';
import { formatDateLong } from '@/utils/dateUtils';
import { formatCurrency } from '../../../utils/formatters';

interface ConfirmedDatesTableProps {
  holds: HoldList[];
  holdsLoading: boolean;
  selectedHoldForDetails: HoldList | null;
  fullHoldDetails: Record<string, Hold>;
  onSelectHoldForDetails: (hold: HoldList) => Promise<void>;
  onRefresh?: () => Promise<void>;
}

export const ConfirmedDatesTable: React.FC<ConfirmedDatesTableProps> = ({
  holds,
  holdsLoading,
  selectedHoldForDetails,
  fullHoldDetails,
  onSelectHoldForDetails,
  onRefresh,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const confirmedHolds = holds.filter((h) => h.status === 'confirmed');
  const handleRefresh = async () => {
    if (!onRefresh || refreshing) return;
    setRefreshing(true);
    setRefreshError(null);
    try {
      await onRefresh();
    } catch (error) {
      setRefreshError(error instanceof Error ? error.message : 'Failed to refresh dates. Reload to verify their current state.');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <>
      {refreshError && <Alert severity="error" sx={{ mb: 2 }}>{refreshError}</Alert>}
      {onRefresh && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button size="small" variant="outlined" onClick={() => void handleRefresh()}
            disabled={holdsLoading || refreshing}
            startIcon={holdsLoading || refreshing ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Refresh
          </Button>
        </Box>
      )}
      {isMobile ? (
        <Stack spacing={1} aria-label="Confirmed show dates" aria-busy={holdsLoading || refreshing}>
          {holdsLoading && <CircularProgress size={24} aria-label="Loading dates" />}
          {confirmedHolds.length === 0 && !holdsLoading && <Typography>No confirmed dates yet.</Typography>}
          {confirmedHolds.map((hold) => <MobileHoldCard key={hold.id} hold={hold}
            details={{ selected: selectedHoldForDetails?.id === hold.id, disabled: holdsLoading || refreshing,
              onSelect: () => void onSelectHoldForDetails(hold) }}>
            <Typography variant="body2">
              Tickets sold: {fullHoldDetails[hold.id] ? fullHoldDetails[hold.id].ticketsSold || 0 : '—'}
              {' · '}Revenue: {formatCurrency(parseFloat(hold.totalRevenue || '0'))}
            </Typography>
            <Typography variant="body2">
              Expenses: {formatCurrency(parseFloat(hold.totalRevenue || '0') - parseFloat(hold.netProfit || '0'))}
              {' · '}Net: {formatCurrency(parseFloat(hold.netProfit || '0'))}
            </Typography>
            <Chip label={hold.isSettled ? 'Settled' : 'Unsettled'} size="small" variant="outlined"
              color={hold.isSettled ? 'success' : 'default'} sx={{ alignSelf: 'flex-start' }} />
          </MobileHoldCard>)}
        </Stack>
      ) : <TableContainer component={Paper} variant="outlined" role="region" tabIndex={0}
        aria-label="Confirmed show dates" aria-busy={holdsLoading || refreshing} sx={{ position: 'relative' }}>
        {/* Loading overlay - shows loading state without replacing content */}
        {holdsLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'background.paper',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
            }}
          >
            <CircularProgress size={32} aria-label="Loading dates" />
          </Box>
        )}
        <Table size="small" aria-label="Confirmed show date performance" sx={{ minWidth: 1100 }}>
          <TableHead>
              <TableRow>
                <TableCell padding="checkbox">Select</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Stage</TableCell>
                <TableCell>Venue</TableCell>
                <TableCell>Timing</TableCell>
                <TableCell align="right">Tickets Sold</TableCell>
                <TableCell align="right">Revenue</TableCell>
                <TableCell align="right">Expenses</TableCell>
                <TableCell align="right">Net Profit</TableCell>
                <TableCell>Settlement</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {confirmedHolds.length === 0 && !holdsLoading && (
                <TableRow>
                  <TableCell colSpan={10} sx={{ py: 3 }}>No confirmed dates yet.</TableCell>
                </TableRow>
              )}
              {confirmedHolds.map((hold) => {
                const holdDetails = fullHoldDetails[hold.id];
                const netProfit = parseFloat(hold.netProfit || '0');

                // List financials cover every date; details load only for the selected hold.
                const totalExpenses = parseFloat(hold.totalRevenue || '0') - netProfit;

                // Ticket counts are unavailable until this hold's details load.
                const ticketsSold = holdDetails ? holdDetails.ticketsSold || 0 : null;

                return (
                  <TableRow
                    key={hold.id}
                    hover
                    onClick={() => navigate(`/bookings/${hold.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Radio
                        checked={selectedHoldForDetails?.id === hold.id}
                        disabled={holdsLoading || refreshing}
                        slotProps={{ input: { 'aria-label': `View details for ${hold.id}` } }}
                        onClick={(e) => {
                          e.stopPropagation();
                          void onSelectHoldForDetails(hold);
                        }}
                      />
                    </TableCell>
                    <TableCell>{formatDateLong(hold.holdDate)}</TableCell>
                    <TableCell>{hold.stageName}</TableCell>
                    <TableCell>{hold.stageVenueName || 'N/A'}</TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'readable.primary',
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        View hold details
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{ticketsSold ?? '—'}</TableCell>
                    <TableCell align="right">
                      <Stack direction="column" spacing={0.5}>
                        <Typography variant="caption">
                          T: {formatCurrency(parseFloat(hold.ticketRevenue || '0'))}
                        </Typography>
                        <Typography variant="caption">
                          B: {formatCurrency(parseFloat(hold.barRevenue || '0'))}
                        </Typography>
                        <Typography variant="caption">
                          O: {formatCurrency(parseFloat(hold.otherRevenue || '0'))}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{formatCurrency(totalExpenses)}</TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          color: netProfit >= 0 ? 'statusText.ready' : 'statusText.attention',
                          fontWeight: 'medium',
                        }}
                      >
                        {formatCurrency(netProfit)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={hold.isSettled ? 'Settled' : 'Unsettled'}
                        color={hold.isSettled ? 'success' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>}
    </>
  );
};
